import React, {
  useCallback,
  useEffect,
  useMemo,
  useState
} from 'react';
import {
  Alert,
  Button,
  Dropdown,
  Input,
  Modal,
  Segmented,
  Space,
  Typography,
  message
} from 'antd';
import { MoreOutlined } from '@ant-design/icons';
import { useSelector } from 'react-redux';
import LevelTag from '../../layout/context/LevelTag';
import type { Level } from '../../layout/context/LevelChip';
import InvitationTable from '../invitations/InvitationTable';
import {
  INVITATION_FILTERS,
  countInvitations,
  filterInvitations,
  isExpired,
  canManageInvitation,
  type InvitationFilter
} from '../invitations/invitationStatus';
import {
  listEntityInvitations,
  listOrganisationInvitations,
  resendEntityInvitation,
  resendOrganisationInvitation,
  revokeEntityInvitation,
  revokeOrganisationInvitation,
  type InvitationRow,
  type OrganisationInviteResult
} from '../../../service/invitation.service';
import { selectTreeIndex } from '../../../redux/context/selectors';

type NodeLevel = 'organisation' | 'entity';

/** The server's answer as one sentence for the reader. */
export const invitationErrorMessage = (error: any, fallback: string): string => {
  const status = error?.response?.status;
  const detail = error?.response?.data?.detail;
  const text = typeof detail === 'string' ? detail : undefined;
  if (status === 403) return text || 'You do not have permission to manage this invitation.';
  if (status === 404) return 'This invitation no longer exists. Refresh the page.';
  return text || fallback;
};

const resentMessage = (result: OrganisationInviteResult): { type: 'success' | 'warning'; text: string } => {
  if (result.status === 'granted') return {
    type: 'success',
    text: 'They already belong to the organisation, so the access was granted directly.'
  };
  if (result.delivery === 'failed') return {
    type: 'warning',
    text: 'Invitation refreshed, but email delivery failed. You can resend it later.'
  };
  return {
    type: 'success',
    text: 'Invitation resent.'
  };
};

const searchable = (row: InvitationRow): string =>
  [row.email, row.name ?? '', row.invited_by_email ?? '', ...row.grants.map((g) => `${g.node_name} ${g.role ?? g.custom_role_name ?? ''}`)]
    .join(' ')
    .toLowerCase();

/**
 * The Invitations tab of an organisation's or an entity's Access page. An organisation lists
 * all of its invitations; an entity only those granting a place inside it, with the grants
 * outside it left out by the server. Resend and Revoke are offered on pending invitations to
 * people who manage the node's users; the server decides (an entity cannot act on an
 * invitation that also grants places elsewhere).
 */
const InvitationsTab: React.FC<{
  nodeId: number;
  level: NodeLevel;
  canAct: boolean;
}> = ({ nodeId, level, canAct }) => {
  const treeIndex = useSelector(selectTreeIndex) as Record<number, { kind?: Level }> | undefined;
  const [rows, setRows] = useState<InvitationRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filter, setFilter] = useState<InvitationFilter>('pending');
  const [query, setQuery] = useState('');
  const [busyId, setBusyId] = useState<number | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setRows(level === 'entity' ? await listEntityInvitations(nodeId) : await listOrganisationInvitations(nodeId));
    } catch (e: any) {
      setRows([]);
      setError(e?.response?.status === 403 || e?.response?.status === 404
        ? `You cannot see the invitations of this ${level}.`
        : 'Unable to load invitations.');
    } finally {
      setLoading(false);
    }
  }, [level, nodeId]);

  useEffect(() => {
    void load();
  }, [load]);

  const counts = useMemo(() => countInvitations(rows), [rows]);
  const shown = useMemo(() => {
    const needle = query.trim().toLowerCase();
    return filterInvitations(rows, filter).filter((row) => !needle || searchable(row).includes(needle));
  }, [rows, filter, query]);

  const resend = async (row: InvitationRow) => {
    setBusyId(row.id);
    try {
      const result = level === 'entity'
        ? await resendEntityInvitation(nodeId, row.id)
        : await resendOrganisationInvitation(nodeId, row.id);
      const outcome = resentMessage(result);
      message[outcome.type](outcome.text);
      await load();
    } catch (e) {
      message.error(invitationErrorMessage(e, 'Could not resend the invitation.'));
    } finally {
      setBusyId(null);
    }
  };

  const revoke = async (row: InvitationRow) => {
    setBusyId(row.id);
    try {
      if (level === 'entity') await revokeEntityInvitation(nodeId, row.id);
      else await revokeOrganisationInvitation(nodeId, row.id);
      message.success('Invitation revoked.');
      await load();
    } catch (e) {
      message.error(invitationErrorMessage(e, 'Could not revoke the invitation.'));
    } finally {
      setBusyId(null);
    }
  };

  const renderWhere = (row: InvitationRow) => (
    <div className="flex flex-col gap-1">
      {row.grants.map((g) => (
        <span key={g.node_id} className="flex items-center gap-2">
          <LevelTag 
            label={g.node_name} 
            level={treeIndex?.[g.node_id]?.kind ?? 'workspace'} /
          >
          <Typography.Text 
            type="secondary"
          >{g.role ?? g.custom_role_name ?? 'Custom role'}</Typography.Text>
        </span>
      ))}
    </div>
  );

  const renderActions = (row: InvitationRow) => {
    if (row.status !== 'PENDING' || row.is_head_invitation || !canManageInvitation(row, level)) return <Typography.Text 
      type="secondary"
    >—</Typography.Text>;
    return (
      <Dropdown
        trigger={['click']}
        menu={{
          items: [
            {
              key: 'resend',
              label: isExpired(row) ? 'Renew and resend' : 'Resend'
            }, {
              type: 'divider'
            }, {
              key: 'revoke',
              label: 'Revoke',
              danger: true
            }
          ],
          onClick: ({ key }) => {
            if (key === 'resend') void resend(row);
            else {
              Modal.confirm({
                title: 'Revoke this invitation?',
                content: `${row.email} will no longer be able to use the link.`,
                okText: 'Revoke',
                okButtonProps: { danger: true },
                onOk: () => revoke(row)
              });
            }
          }
        }}
      >
        <Button
          type="text"
          aria-label="Actions"
          icon={<MoreOutlined />}
          loading={busyId === row.id}
        />
      </Dropdown>
    );
  };

  return (
    <div className="flex flex-col gap-4">
      <div className="text-sm text-gray-500">
        {level === 'entity'
          ? 'Invitations that grant access inside this entity. Places elsewhere in the organisation are not shown.'
          : 'Every invitation this organisation has sent.'}
      </div>
      {error && <Alert type="info" showIcon message={error} />}
      <Space wrap>
        <Segmented<InvitationFilter>
          value={filter}
          onChange={setFilter}
          options={INVITATION_FILTERS.map((option) => ({
            value: option.value,
            label: `${option.label} (${counts[option.value]})`
          }))}
        />
        <Input.Search
          allowClear
          placeholder="Search email, place or role..."
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          style={{ width: 260 }}
        />
      </Space>
      <InvitationTable<InvitationRow>
        rows={shown}
        loading={loading}
        whereTitle="Where"
        renderWhere={renderWhere}
        renderInvitedBy={(row) => row.invited_by_email || <Typography.Text 
          type="secondary"
        >—</Typography.Text>}
        renderActions={canAct ? renderActions : undefined}
      />
    </div>
  );
};

export default InvitationsTab;
